import React, { useEffect, useRef, useState } from 'react';
import { Grid, Box } from '@mui/material';
import { MapType } from '../helpers/mapTypes';
import { Boundary, Mission, Zone, ZoneType } from '../../../db';
import { dispatchMissionUpdated } from '../../../missionEvents';
import { dispatchZonesUpdated } from '../../../mapEvents';
import XBox from '@mui/material/Box';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import MapLayerItem from './MapLayerItem';
import { TreeItemProps } from '@mui/x-tree-view';
import { isNumeric } from '../../../utils';
import HolesMergeDialog from './HolesMergeDialog';
import PolysMergeDialog from './PolysMergeDialog';
import zonesManager from '../../../services/zones/ZonesManager';
import { dispatchMapLayersFormPolySelected } from './map-layers-form-events';
import { SelectCandidate } from '../../../types/types';

const ITEM_TYPES = ['zone', 'poly', 'outer-boundary', 'inner-boundary', 'header'] as const;
export type ItemType = (typeof ITEM_TYPES)[number];

type TreeViewEnhancedItem = TreeViewBaseItem & {
  itemType: ItemType;
  zoneId?: number;
  polyId?: number;
  boundaryId?: number;
  labelEditable?: boolean;
  hideable?: boolean;
  hasHideableLabels?: boolean;
  isBoundaryAreaValid?: boolean;
};

const generateZonesTreeItem = (zones: Zone[], zonesTreeItem: ZonesTreeItem): TreeViewEnhancedItem => {
  const out: TreeViewEnhancedItem = {
    id: zonesTreeItem.id,
    label: zonesTreeItem.label,
    children: [] as TreeViewEnhancedItem[],
    itemType: 'header',
  };

  zones.forEach((zone) => {
    const zoneLeaf = {
      id: genZoneLeafId(zone.instance_id),
      label: zone.zone_name,
      children: [] as TreeViewEnhancedItem[],
      itemType: 'zone',
      zoneId: zone.instance_id,
      labelEditable: zonesTreeItem.id === 'sample-zones',
      hideable: true,
      hasHideableLabels: [ZoneType.FIELD, ZoneType.COLLECTED_FIELD].includes(zone.zone_type),
    };

    const polys = zone.getPolysGroupedByPolyId();
    const polyIds = Object.keys(polys);

    polyIds.forEach((polyIdString) => {
      const polyId = parseInt(polyIdString);

      const polyLeaf: TreeViewEnhancedItem = {
        id: genPolyLeafId(zone.instance_id, polyId),
        label: `Poly ${polyId}`,
        children: [] as TreeViewEnhancedItem[],
        itemType: 'poly',
        zoneId: zone.instance_id,
        polyId: polyId,
      };

      const poly = polys[polyId];
      const outerBoundary = poly.outerBoundary;

      if (outerBoundary) {
        const area = outerBoundary.getArea();
        const isBoundaryAreaValid = Boundary.isBoundaryAreaValid(area);

        polyLeaf.children!.push({
          id: genOuterBoundaryLeafId(zone.instance_id, polyId, outerBoundary.instance_id),
          label: `Outer Boundary ${outerBoundary.instance_id}, area ${area.toLocaleString()} m², coords # ${outerBoundary.getCoordsNumber()}`,
          children: [] as TreeViewEnhancedItem[],
          itemType: 'outer-boundary',
          zoneId: zone.instance_id,
          polyId: outerBoundary.poly_id,
          boundaryId: outerBoundary.instance_id,
          isBoundaryAreaValid,
        } as TreeViewEnhancedItem);
      }

      const innerBoundaries = poly.innerBoundaries;

      innerBoundaries.forEach((ib, holeIndex) => {
        const holeLabel = Boundary.holeLabel(polyId, holeIndex);

        polyLeaf.children!.push({
          id: genInnerBoundaryLeafId(zone.instance_id, polyId, ib.instance_id),
          label: `Hole ${holeLabel}`,
          children: [] as TreeViewEnhancedItem[],
          itemType: 'inner-boundary',
          zoneId: zone.instance_id,
          polyId: ib.poly_id,
          boundaryId: ib.instance_id,
        } as TreeViewEnhancedItem);
      });

      zoneLeaf.children.push(polyLeaf);
    });

    out.children!.push(zoneLeaf);
  });

  return out;
};

const ZONES_ITEMS = [
  { id: 'field-zones', label: 'Field Zones' },
  { id: 'sample-zones', label: 'Sample Zones' },
  { id: 'pullin-zones', label: 'Pullin Zones' },
  { id: 'slow-zones', label: 'Slow Zones' },
  { id: 'unsafe-zones', label: 'Unsafe Zones' },
  { id: 'collected-field-zones', label: 'Collected Field Zones' },
] as const;

type ZonesTreeItem = (typeof ZONES_ITEMS)[number];

const generateTreeItems = (
  fieldZones: Zone[],
  sampleZones: Zone[],
  pullinZones: Zone[],
  slowZones: Zone[],
  unsafeZones: Zone[],
  collectedFieldZones: Zone[],
): TreeViewEnhancedItem[] => {
  return [
    generateZonesTreeItem(fieldZones, ZONES_ITEMS[0]),
    generateZonesTreeItem(sampleZones, ZONES_ITEMS[1]),
    generateZonesTreeItem(pullinZones, ZONES_ITEMS[2]),
    generateZonesTreeItem(slowZones, ZONES_ITEMS[3]),
    generateZonesTreeItem(unsafeZones, ZONES_ITEMS[4]),
    generateZonesTreeItem(collectedFieldZones, ZONES_ITEMS[5]),
  ];
};

type MapLayersFormProps = {
  addCheckpoint: (name: string) => void;
  mapType: MapType;
  mission: Mission;
  onAddPolyToZoneBtnClick: (zoneId: number) => void;
  zoneTypeChangeCandidate: SelectCandidate | null;
};

const MapLayersForm: React.FC<MapLayersFormProps> = ({
  addCheckpoint,
  mapType,
  mission,
  onAddPolyToZoneBtnClick,
  zoneTypeChangeCandidate,
}) => {
  const [hiddenZones, setHiddenZones] = useState<Set<number>>(new Set());
  const [zonesWithHiddenLabels, setZonesWithHiddenLabels] = useState<Set<number>>(new Set());
  const [showHolesMergeDialog, setShowHolesMergeDialog] = useState<boolean>(false);
  const [showPolysMergeDialog, setShowPolysMergeDialog] = useState<boolean>(false);
  const [selectedBoundaryId, setSelectedBoundaryId] = useState<number | null>(null);
  const [selectedPolyForMerge, setSelectedPolyForMerge] = useState<{ zoneId: number; polyId: number } | null>(null);
  const [selectedTreeItemId, setSelectedTreeItemId] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const isInitialMount = useRef(true);

  useEffect(() => {
    mission.getZones().forEach((zone: Zone) => {
      zone.visible = !hiddenZones.has(zone.instance_id);
    });

    // Skip initial mount — zones start visible by default and
    // ZonesLayer already does rebuildAll() in componentDidMount.
    if (isInitialMount.current) {
      return;
    }

    // Only ZonesLayer cares about zone-visibility changes — firing
    // the catch-all mission event would rebuild ~1000 cores on Rose 6
    // for a checkbox toggle. Step 12 of
    // docs/superpowers/plans/2026-04-08-kml-layer-split.md.
    dispatchZonesUpdated();
  }, [hiddenZones]);

  useEffect(() => {
    mission.getZones().forEach((zone: Zone) => {
      zone.labels_visible = !zonesWithHiddenLabels.has(zone.instance_id);
    });

    if (isInitialMount.current) {
      isInitialMount.current = false;

      return;
    }

    // Label toggle is zone-render-only; no core/sample effect.
    dispatchZonesUpdated();
  }, [zonesWithHiddenLabels]);

  // Sync map selection to tree view
  useEffect(() => {
    if (!zoneTypeChangeCandidate) {
      setSelectedTreeItemId(null);

      return;
    }

    const properties = zoneTypeChangeCandidate.feature.getProperties();
    const polyId = properties.poly_id;
    const zoneInstanceId = properties.zone_instance_id;

    if (polyId === undefined || zoneInstanceId === undefined) {
      console.warn('poly_id or zone_instance_id not found in selected feature properties');

      return;
    }

    // Construct the tree item ID: zone-{zoneId}-poly-{polyId}
    const treeItemId = genPolyLeafId(zoneInstanceId, polyId);
    setSelectedTreeItemId(treeItemId);

    // Auto-expand parent items to make the selected item visible
    // For a poly item like "zone-123-poly-28", we need to expand:
    // 1. The zone type header (e.g., "collected-field-zones")
    // 2. The zone itself (e.g., "zone-123")
    const zone = mission.getZoneById(zoneInstanceId);

    if (zone) {
      const parentsToExpand: string[] = [];

      // Find which header this zone belongs to
      let headerItemId: string | null = null;

      switch (zone.zone_type) {
        case ZoneType.FIELD:
          headerItemId = 'field-zones';
          break;
        case ZoneType.SAMPLE_ZONE:
          headerItemId = 'sample-zones';
          break;
        case ZoneType.PULLIN:
          headerItemId = 'pullin-zones';
          break;
        case ZoneType.SLOW:
          headerItemId = 'slow-zones';
          break;
        case ZoneType.UNSAFE:
          headerItemId = 'unsafe-zones';
          break;
        case ZoneType.COLLECTED_FIELD:
          headerItemId = 'collected-field-zones';
          break;
      }

      if (headerItemId) {
        parentsToExpand.push(headerItemId);
      }

      // Add the zone item itself
      const zoneItemId = genZoneLeafId(zoneInstanceId);
      parentsToExpand.push(zoneItemId);

      // Merge with existing expanded items, avoiding duplicates
      setExpandedItems((prev) => {
        const newExpanded = new Set([...prev, ...parentsToExpand]);

        return Array.from(newExpanded);
      });
    }
  }, [zoneTypeChangeCandidate, mission]);

  const hideZone = (zoneId: number) => {
    setHiddenZones((prev) => {
      const newSet = new Set(prev);
      newSet.add(zoneId);

      return newSet;
    });
  };

  const showZone = (zoneId: number) => {
    setHiddenZones((prev) => {
      const newSet = new Set(prev);
      newSet.delete(zoneId);

      return newSet;
    });
  };

  const hideZoneLabels = (zoneId: number) => {
    setZonesWithHiddenLabels((prev) => {
      const newSet = new Set(prev);
      newSet.add(zoneId);

      return newSet;
    });
  };

  const showZoneLabels = (zoneId: number) => {
    setZonesWithHiddenLabels((prev) => {
      const newSet = new Set(prev);
      newSet.delete(zoneId);

      return newSet;
    });
  };

  const handleNameChange = async (zoneId: number, newName: string) => {
    const zone = mission.getZones().find((z) => z.instance_id === zoneId);

    if (zone && zone.zone_name !== newName) {
      addCheckpoint(`Renaming a zone to ${newName}`);

      await zone.renameWithUnderlyingSample(newName);
    }
  };

  const handleZoneDelete = (zoneId: number) => {
    addCheckpoint(`Deleting a zone`);

    mission.removeZoneWithBoundaries(zoneId, false);

    dispatchMissionUpdated();
    // removeZoneWithBoundaries fires BOUNDARY_UPDATED internally via
    // updateOrDeleteBoundary for most paths, but the edge case (zone
    // with no outer boundaries) doesn't. Explicit zone signal ensures
    // ZonesLayer always rebuilds.
    dispatchZonesUpdated();
  };

  const handleAddingPolyToZone = (zoneId: number) => {
    onAddPolyToZoneBtnClick(zoneId);
  };

  const handlePolyDelete = async (polyId: number) => {
    addCheckpoint(`Deleting an outer boundary and holes inside`);

    await mission.removeOuterAndInnerBoundariesByPolyId(polyId, false);

    dispatchMissionUpdated();
  };

  const handleHoleDelete = (boundaryId: number) => {
    addCheckpoint(`Deleting a hole`);

    mission.removeInnerBoundary(boundaryId);

    dispatchMissionUpdated();
    // removeInnerBoundary doesn't fire any events internally.
    dispatchZonesUpdated();
  };

  const handleHolesMergeClick = (boundaryId: number) => {
    setSelectedBoundaryId(boundaryId);

    setShowHolesMergeDialog(true);
  };

  const handlePolysMergeClick = (zoneId: number, polyId: number) => {
    setSelectedPolyForMerge({ zoneId, polyId });

    setShowPolysMergeDialog(true);
  };

  const mergeHoles = (selectedBoundaryId: number, mergeWithBoundaryId: number) => {
    addCheckpoint(`Merging hole ${selectedBoundaryId} with hole ${mergeWithBoundaryId}`);

    zonesManager.mergePolygonHoles(mission, selectedBoundaryId, mergeWithBoundaryId);

    setShowHolesMergeDialog(false);
  };

  const mergePolys = (selectedPolyId: number, mergeWithPolyId: number) => {
    addCheckpoint(`Merging poly ${selectedPolyId} with poly ${mergeWithPolyId}`);

    zonesManager.mergeZonePolys(mission, selectedPolyId, mergeWithPolyId);

    setShowPolysMergeDialog(false);
  };

  const treeItems = generateTreeItems(
    mission.getFieldZones().sort((a, b) => a.zone_name.localeCompare(b.zone_name)),
    mission.getZonesOfTypeSampleZone().sort((a, b) => {
      if (isNumeric(a.zone_name) && isNumeric(b.zone_name)) {
        return Number(a.zone_name) - Number(b.zone_name);
      }

      return a.zone_name.localeCompare(b.zone_name);
    }),
    mission.getPullinZones().sort((a, b) => a.zone_name.localeCompare(b.zone_name)),
    mission.getSlowZones().sort((a, b) => a.zone_name.localeCompare(b.zone_name)),
    mission.getUnsafeZones().sort((a, b) => a.zone_name.localeCompare(b.zone_name)),
    mission.getCollectedFieldZones().sort((a, b) => a.zone_name.localeCompare(b.zone_name)),
  );

  const itemMap = React.useMemo(() => {
    const map = new Map<string, TreeViewEnhancedItem>();

    const buildMap = (items: TreeViewEnhancedItem[]) => {
      items.forEach((item) => {
        map.set(item.id, item);

        if (item.children) {
          buildMap(item.children as TreeViewEnhancedItem[]);
        }
      });
    };

    buildMap(treeItems);

    return map;
  }, [treeItems]);

  const handleTreeItemClick = (itemId: string) => {
    const item = itemMap.get(itemId);

    if (!item) {
      return;
    }

    switch (item.itemType) {
      case 'zone':
        // const zone = mission.getZoneById(item.zoneId!);
        // if (zone) {
        //   console.log('zone', zone);
        //   console.log('zone polys', zone.getPolysGroupedByPolyId());
        // }
        break;
      case 'poly':
        if (item.polyId !== undefined) {
          dispatchMapLayersFormPolySelected(item.polyId);
        }

        break;
      default:
        break;
    }
  };

  return (
    <form>
      <Box p={2} maxWidth="100%" boxSizing="border-box">
        <Grid container spacing={3} direction="column" size={12}>
          <XBox sx={{ minHeight: 352, maxHeight: 'calc(100vh - 250px)', minWidth: 450, overflow: 'auto' }}>
            <RichTreeView
              items={treeItems}
              selectedItems={selectedTreeItemId}
              onSelectedItemsChange={(_event, itemId) => setSelectedTreeItemId(itemId as string | null)}
              expandedItems={expandedItems}
              onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds as string[])}
              isItemEditable={(item) => Boolean(item?.labelEditable)}
              onItemLabelChange={async (itemId, label) => await handleNameChange(getZoneIdFromItemId(itemId), label)}
              slots={{ item: MapLayerItem as unknown as React.ComponentType<TreeItemProps> }}
              onItemClick={(_: React.MouseEvent, itemId) => handleTreeItemClick(itemId)}
              slotProps={{
                item: (ownerState) => {
                  const originalItem = itemMap.get(ownerState.itemId);

                  return {
                    hiddenZones,
                    showZone,
                    hideZone,
                    zonesWithHiddenLabels,
                    showZoneLabels,
                    hideZoneLabels,
                    handleZoneDelete,
                    handleAddingPolyToZone,
                    handleHoleDelete,
                    handleHolesMergeClick,
                    handlePolysMergeClick,
                    handlePolyDelete,
                    hideable: originalItem?.hideable,
                    hasHideableLabels: originalItem?.hasHideableLabels,
                    itemType: originalItem?.itemType,
                    isBoundaryAreaValid: originalItem?.isBoundaryAreaValid,
                    zoneId: originalItem?.zoneId,
                    polyId: originalItem?.polyId,
                    boundaryId: originalItem?.boundaryId,
                  } as Partial<TreeItemProps>;
                },
              }}
            />
          </XBox>
        </Grid>
      </Box>

      {selectedBoundaryId && showHolesMergeDialog && (
        <HolesMergeDialog
          open={showHolesMergeDialog}
          mission={mission}
          boundaryId={selectedBoundaryId}
          onClose={() => setShowHolesMergeDialog(false)}
          onMergeClick={mergeHoles}
        />
      )}

      {selectedPolyForMerge && showPolysMergeDialog && (
        <PolysMergeDialog
          open={showPolysMergeDialog}
          mission={mission}
          zoneId={selectedPolyForMerge.zoneId}
          polyId={selectedPolyForMerge.polyId}
          onClose={() => setShowPolysMergeDialog(false)}
          onMergeClick={mergePolys}
        />
      )}
    </form>
  );
};

const genZoneLeafId = (zoneInstanceId: number): string => {
  return `zone-${zoneInstanceId}`;
};

const genPolyLeafId = (zoneInstanceId: number, polyId: number): string => {
  return `${genZoneLeafId(zoneInstanceId)}-poly-${polyId}`;
};

const genOuterBoundaryLeafId = (zoneInstanceId: number, polyId: number, outerBoundaryId: number): string => {
  return `${genPolyLeafId(zoneInstanceId, polyId)}-outer-boundary-${outerBoundaryId}`;
};

const genInnerBoundaryLeafId = (zoneInstanceId: number, polyId: number, innerBoundaryId: number): string => {
  return `${genPolyLeafId(zoneInstanceId, polyId)}-inner-boundary-${innerBoundaryId}`;
};

const getZoneIdFromItemId = (itemId: string): number => {
  const match = itemId.match(
    /^zone-(\d+)(-outer-boundaries|-inner-boundaries|-outer-boundary|-inner-boundary)?(-\d+)?$/,
  );

  if (match) {
    return parseInt(match[1], 10);
  }

  return NaN;
};

export default MapLayersForm;
